import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useAlerts } from '../context/AlertContext';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  getMunicipalDashboard,
  getBarangays,
  getIncidents,
  getDiseaseAlerts,
  acknowledgeAlert,
} from '../lib/api';
import {
  Users,
  Stethoscope,
  Pill,
  Ambulance,
  AlertTriangle,
  Syringe,
  Package,
  TrendingUp,
  Building2,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Bell,
} from 'lucide-react';

export default function MunicipalDashboard() {
  const { user } = useAuth();
  const { setAlerts: setCriticalAlerts, isAlertMode, enableAudio, audioEnabled } = useAlerts();
  const [dashboard, setDashboard] = useState(null);
  const [barangays, setBarangays] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, []);

  // Update critical alerts when incidents or disease alerts change
  useEffect(() => {
    const criticalItems = [];
    
    // Add pending incidents as critical alerts
    incidents.forEach(inc => {
      if (inc.status === 'pending') {
        criticalItems.push({
          id: inc.id,
          type: 'incident',
          message: `${inc.incident_type} emergency: ${inc.location_text || 'Location pending'}`,
          severity: inc.escalated ? 'critical' : 'high',
        });
      }
    });
    
    // Add disease alerts
    alerts.forEach(alert => {
      criticalItems.push({
        id: alert.id,
        type: 'disease',
        message: alert.alert_message,
        severity: 'critical',
      });
    });
    
    setCriticalAlerts(criticalItems);
  }, [incidents, alerts, setCriticalAlerts]);

  const loadData = async () => {
    try {
      const [dashRes, brgyRes, incRes, alertRes] = await Promise.all([
        getMunicipalDashboard(),
        getBarangays(),
        getIncidents({ status: 'pending' }),
        getDiseaseAlerts({ acknowledged: false }),
      ]);
      setDashboard(dashRes.data);
      setBarangays(brgyRes.data);
      setIncidents(incRes.data.slice(0, 5));
      setAlerts(alertRes.data);
    } catch (err) {
      console.error('Failed to load dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledgeAlert = async (alertId) => {
    try {
      await acknowledgeAlert(alertId);
      setAlerts(alerts.filter((a) => a.id !== alertId));
    } catch (err) {
      console.error('Failed to acknowledge alert:', err);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-muted-foreground">Loading dashboard...</div>
        </div>
      </DashboardLayout>
    );
  }

  const statsCards = [
    { icon: Building2, label: 'Barangays', value: dashboard?.barangays || 0, color: 'text-primary' },
    { icon: Stethoscope, label: 'Total Patients', value: dashboard?.total_patients || 0, color: 'text-blue-600' },
    { icon: Ambulance, label: 'Active Incidents', value: dashboard?.active_incidents || 0, color: 'text-amber-600', alert: dashboard?.active_incidents > 0 },
    { icon: AlertTriangle, label: 'Escalated', value: dashboard?.escalated_incidents || 0, color: 'text-destructive', alert: dashboard?.escalated_incidents > 0 },
    { icon: Pill, label: 'Low Stock Alerts', value: dashboard?.low_stock_medicines || 0, color: 'text-orange-600', alert: dashboard?.low_stock_medicines > 0 },
    { icon: Clock, label: 'Expiring Soon', value: dashboard?.expiring_medicines || 0, color: 'text-yellow-600' },
    { icon: AlertCircle, label: 'Disease Alerts', value: dashboard?.active_alerts || 0, color: 'text-red-600', alert: dashboard?.active_alerts > 0 },
    { icon: Package, label: 'Pending Requests', value: dashboard?.pending_requests || 0, color: 'text-indigo-600' },
    { icon: Syringe, label: 'Overdue Vaccines', value: dashboard?.overdue_vaccinations || 0, color: 'text-purple-600' },
    { icon: Users, label: 'Vulnerable Pop.', value: dashboard?.vulnerable_population || 0, color: 'text-teal-600' },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="municipal-dashboard">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold font-['Public_Sans'] text-foreground">
              Municipal Command Center
            </h1>
            <p className="text-muted-foreground mt-1">
              Welcome back, {user?.full_name}. Here's your municipality overview.
            </p>
          </div>
          <Badge variant="outline" className="w-fit">
            <Clock className="h-3 w-3 mr-1" />
            Last updated: {new Date().toLocaleTimeString()}
          </Badge>
        </div>

        {/* Disease Alerts Banner */}
        {alerts.length > 0 && (
          <Card className="border-destructive/50 bg-destructive/5">
            <CardHeader className="pb-3">
              <CardTitle className="text-destructive flex items-center gap-2 text-lg">
                <AlertTriangle className="h-5 w-5" />
                Active Disease Alerts ({alerts.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {alerts.map((alert) => (
                <div
                  key={alert.id}
                  className="flex items-center justify-between p-3 bg-white rounded-sm border border-destructive/20"
                  data-testid={`disease-alert-${alert.id}`}
                >
                  <div>
                    <p className="font-medium text-foreground">{alert.alert_message}</p>
                    <p className="text-sm text-muted-foreground">
                      {barangays.find((b) => b.id === alert.barangay_id)?.name} •{' '}
                      {alert.fogging_suggested && 'Fogging recommended'}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleAcknowledgeAlert(alert.id)}
                    data-testid={`acknowledge-alert-${alert.id}`}
                  >
                    Acknowledge
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {statsCards.map((stat, idx) => (
            <Card
              key={idx}
              className={`${stat.alert ? 'border-amber-300 bg-amber-50/50' : ''}`}
              data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <stat.icon className={`h-5 w-5 ${stat.color}`} />
                  {stat.alert && <span className="h-2 w-2 bg-amber-500 rounded-full animate-pulse" />}
                </div>
                <p className="text-2xl font-bold mt-2">{stat.value}</p>
                <p className="text-xs text-muted-foreground">{stat.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Active Incidents */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Ambulance className="h-5 w-5 text-amber-600" />
                Active Emergencies
              </CardTitle>
              <CardDescription>Incidents requiring attention</CardDescription>
            </CardHeader>
            <CardContent>
              {incidents.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckCircle className="h-12 w-12 mx-auto mb-2 text-green-500" />
                  <p>No active emergencies</p>
                </div>
              ) : (
                <ScrollArea className="h-[300px]">
                  <div className="space-y-3">
                    {incidents.map((incident) => (
                      <div
                        key={incident.id}
                        className="p-4 border border-slate-200 rounded-sm hover:bg-slate-50 transition-colors"
                        data-testid={`incident-${incident.id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <Badge
                                variant={
                                  incident.incident_type === 'Medical'
                                    ? 'default'
                                    : incident.incident_type === 'Fire'
                                    ? 'destructive'
                                    : 'secondary'
                                }
                              >
                                {incident.incident_type}
                              </Badge>
                              {incident.escalated && (
                                <Badge variant="destructive" className="text-xs">
                                  ESCALATED
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm mt-2 text-muted-foreground">
                              {incident.location_text || 'Location not specified'}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {barangays.find((b) => b.id === incident.barangay_id)?.name}
                            </p>
                          </div>
                          <div className="text-right">
                            <Badge
                              variant="outline"
                              className={
                                incident.status === 'pending'
                                  ? 'border-amber-500 text-amber-600'
                                  : incident.status === 'acknowledged'
                                  ? 'border-blue-500 text-blue-600'
                                  : 'border-green-500 text-green-600'
                              }
                            >
                              {incident.status}
                            </Badge>
                            <p className="text-xs text-muted-foreground mt-1">
                              {new Date(incident.time_received).toLocaleTimeString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>

          {/* Top Conditions */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-primary" />
                Top Health Conditions
              </CardTitle>
              <CardDescription>Most reported complaints</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {dashboard?.top_conditions?.map((condition, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-sm"
                  >
                    <div className="flex items-center gap-3">
                      <span className="h-6 w-6 rounded-full bg-primary/10 text-primary text-xs font-medium flex items-center justify-center">
                        {idx + 1}
                      </span>
                      <span className="font-medium text-sm">{condition._id}</span>
                    </div>
                    <Badge variant="secondary">{condition.count}</Badge>
                  </div>
                ))}
                {(!dashboard?.top_conditions || dashboard.top_conditions.length === 0) && (
                  <p className="text-center text-muted-foreground py-4">No data available</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Barangays Overview */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-primary" />
              Barangay Overview
            </CardTitle>
            <CardDescription>Quick status of all barangays</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
              {barangays.map((brgy) => (
                <div
                  key={brgy.id}
                  className="p-4 border border-slate-200 rounded-sm hover:border-primary/50 transition-colors"
                  data-testid={`barangay-card-${brgy.id}`}
                >
                  <h4 className="font-medium text-sm">{brgy.name}</h4>
                  <p className="text-xs text-muted-foreground mt-1">Code: {brgy.code}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
